import { trackEvent } from '@/utils/analytics';

/**
 * Track when a user views a post in detail
 * @param postId - The ID of the post
 * @param postType - The type of post (text, image, video, poll)
 * @param properties - Additional properties to track
 */
export const trackPostViewed = (
  postId: string,
  postType: 'text' | 'image' | 'video' | 'poll',
  properties?: Record<string, any>
) => {
  trackEvent('post_viewed', {
    post_id: postId,
    post_type: postType,
    interaction_source: properties?.source || 'feed',
    ...properties
  });
};

/**
 * Track when a user creates a post
 * @param postId - The ID of the post
 * @param postType - The type of post (text, image, video, poll)
 * @param properties - Additional properties to track
 */
export const trackPostCreated = (
  postId: string,
  postType: 'text' | 'image' | 'video' | 'poll',
  properties?: Record<string, any>
) => {
  trackEvent('post_created', {
    post_id: postId,
    post_type: postType,
    post_category: properties?.category || 'general',
    has_media: properties?.hasMedia || false,
    ...properties
  });
};

/**
 * Track when a user likes a post
 * @param postId - The ID of the post
 * @param properties - Additional properties to track
 */
export const trackPostLiked = (
  postId: string,
  properties?: Record<string, any>
) => {
  trackEvent('post_liked', {
    post_id: postId,
    interaction_source: properties?.source || 'feed',
    ...properties
  });
};

/**
 * Track when a user shares a post
 * @param postId - The ID of the post
 * @param shareMethod - The method of sharing
 * @param properties - Additional properties to track
 */
export const trackPostShared = (
  postId: string,
  shareMethod: 'copy_link' | 'social' | 'direct_message',
  properties?: Record<string, any>
) => {
  trackEvent('post_shared', {
    post_id: postId,
    share_method: shareMethod,
    interaction_source: properties?.source || 'feed',
    ...properties
  });
};

/**
 * Track when a user comments on a post
 * @param postId - The ID of the post
 * @param commentId - The ID of the comment
 * @param properties - Additional properties to track
 */
export const trackPostCommented = (
  postId: string,
  commentId: string,
  properties?: Record<string, any>
) => {
  trackEvent('post_commented', {
    post_id: postId,
    comment_id: commentId,
    interaction_source: properties?.source || 'feed',
    ...properties
  });
};

/**
 * Track when a user bookmarks a post
 * @param postId - The ID of the post
 * @param properties - Additional properties to track
 */
export const trackPostSaved = (
  postId: string,
  properties?: Record<string, any>
) => {
  trackEvent('post_saved', {
    post_id: postId,
    interaction_source: properties?.source || 'feed',
    ...properties
  });
};

/**
 * Track when a user scrolls through the feed
 * @param depth - The scroll depth (percentage or number of posts)
 * @param timeSpent - Time spent scrolling in seconds
 * @param properties - Additional properties to track
 */
export const trackFeedScrolled = (
  depth: number,
  timeSpent: number,
  properties?: Record<string, any>
) => {
  trackEvent('feed_scrolled', {
    scroll_depth: depth,
    time_spent: timeSpent,
    feed_type: properties?.feedType || 'main',
    ...properties
  });
};

/**
 * Track when a user views media content
 * @param mediaId - The ID of the media
 * @param mediaType - The type of media
 * @param properties - Additional properties to track
 */
export const trackMediaViewed = (
  mediaId: string,
  mediaType: 'image' | 'video' | 'audio',
  properties?: Record<string, any>
) => {
  trackEvent('media_viewed', {
    media_id: mediaId,
    media_type: mediaType,
    post_id: properties?.postId,
    interaction_source: properties?.source || 'post',
    ...properties
  });
};

/**
 * Track when a user votes in a poll
 * @param pollId - The ID of the poll
 * @param optionId - The ID of the selected option
 * @param properties - Additional properties to track
 */
export const trackPollVoted = (
  pollId: string,
  optionId: string,
  properties?: Record<string, any>
) => {
  trackEvent('poll_voted', {
    poll_id: pollId,
    option_id: optionId,
    post_id: properties?.postId,
    interaction_source: properties?.source || 'feed',
    ...properties
  });
};

/**
 * Track when a user reposts content
 * @param postId - The ID of the post
 * @param repostType - The type of repost (repost or quote)
 * @param properties - Additional properties to track
 */
export const trackRepost = (
  postId: string,
  repostType: 'repost' | 'quote',
  properties?: Record<string, any>
) => {
  trackEvent('post_reposted', {
    post_id: postId,
    repost_type: repostType,
    interaction_source: properties?.source || 'feed',
    ...properties
  });
};